/**
 * Project hub → "Data" tab → connections manager (track B1).
 *
 * A connection is the "how do we reach and authenticate this upstream"
 * half of a data source: kind + non-secret config (host, port, base
 * url, pagination) + `secret_ref`, a POINTER to the credentials.
 *
 * INVARIANT this component exists to preserve: credential VALUES never
 * pass through the browser. There is deliberately no password field,
 * no token field and no `<input type="password">` anywhere below — the
 * operator sets the values in the environment variable named by the
 * secret reference (`env:NAME` → `BUILDER_SECRET_NAME`, a JSON
 * object), or, once the Vault client lands, in Vault. A vitest asserts
 * the absence of such fields, so adding one will fail the build.
 */

import { Badge, Button, Card, CardBody, Input, Select } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  projectsApi,
  type ConnectionKind,
  type ConnectionResponse,
  type ConnectionTestResponse,
} from "@/lib/builder-api";
import type { Project } from "@/types";

const KINDS: ConnectionKind[] = ["parquet", "duckdb_attach", "postgres", "api"];

/** Starter config per kind, so the JSON box is never a blank page. */
const CONFIG_TEMPLATES: Record<ConnectionKind, string> = {
  parquet: JSON.stringify({ url_scheme: "s3" }, null, 2),
  duckdb_attach: JSON.stringify({ url_scheme: "s3" }, null, 2),
  postgres: JSON.stringify(
    { host: "db.internal", port: 5432, database: "warehouse" },
    null,
    2,
  ),
  api: JSON.stringify(
    {
      url: "https://api.example.com/v1/items",
      records_path: "data.items",
      auth: { scheme: "bearer" },
      pagination: { mode: "page", page_param: "page" },
    },
    null,
    2,
  ),
};

interface DraftState {
  id: string | null;
  name: string;
  kind: ConnectionKind;
  configText: string;
  secretRef: string;
}

function emptyDraft(): DraftState {
  return {
    id: null,
    name: "",
    kind: "postgres",
    configText: CONFIG_TEMPLATES.postgres,
    secretRef: "",
  };
}

export function ProjectConnectionsPanel({
  project,
  onChanged,
}: {
  project: Project;
  /** Fired after a create / edit / delete. The data-source form below
      keeps its OWN copy of this list to fill the connection picker, so
      without this it kept showing "(none)" until a full page reload —
      right after the operator created the connection to get past it. */
  onChanged?: () => void;
}): JSX.Element {
  const { t } = useT();
  const [rows, setRows] = useState<ConnectionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<
    { id: string; result: ConnectionTestResponse } | null
  >(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const out = await projectsApi.connections.list(project.slug);
      setRows(out.connections);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [project.slug]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onSave = async (): Promise<void> => {
    if (draft === null) return;
    setSaving(true);
    setError(null);
    let config: Record<string, unknown>;
    try {
      config =
        draft.configText.trim() === ""
          ? {}
          : (JSON.parse(draft.configText) as Record<string, unknown>);
    } catch (err) {
      setError(
        t("projects.connections.configInvalidJson", { detail: String(err) }),
      );
      setSaving(false);
      return;
    }
    try {
      if (draft.id === null) {
        await projectsApi.connections.create(project.slug, {
          name: draft.name.trim(),
          kind: draft.kind,
          config,
          secret_ref: draft.secretRef.trim(),
        });
      } else {
        await projectsApi.connections.update(project.slug, draft.id, {
          name: draft.name.trim(),
          config,
          secret_ref: draft.secretRef.trim(),
        });
      }
      setDraft(null);
      await refresh();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row: ConnectionResponse): Promise<void> => {
    if (
      !window.confirm(
        t("projects.connections.deleteConfirm", { name: row.name }),
      )
    )
      return;
    setError(null);
    try {
      await projectsApi.connections.remove(project.slug, row.id);
      await refresh();
      onChanged?.();
    } catch (err) {
      // 409 when a project's data_source still points at it — the
      // backend refuses to cascade, and the message names the project.
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onTest = async (row: ConnectionResponse): Promise<void> => {
    setTestingId(row.id);
    setTestResult(null);
    setError(null);
    try {
      const result = await projectsApi.connections.test(project.slug, row.id);
      setTestResult({ id: row.id, result });
    } catch (err) {
      // A missing/unresolvable secret is a 400, not `{ok:false}` —
      // surface it as an error, not as a test verdict.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setTestingId(null);
    }
  };

  return (
    <Card variant="bordered">
      <CardBody>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold">
              {t("projects.connections.title")}
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              {t("projects.connections.intro")}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setDraft(emptyDraft());
              setTestResult(null);
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("projects.connections.addBtn")}
          </Button>
        </div>

        {error !== null && (
          <p
            role="alert"
            className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700"
          >
            {error}
          </p>
        )}

        {loading ? (
          <div className="mt-4 flex justify-center">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <p className="mt-4 text-xs text-neutral-500">
            {t("projects.connections.empty")}
          </p>
        ) : (
          <ul className="mt-4 space-y-2">
            {rows.map((row) => (
              <li
                key={row.id}
                className="rounded border border-neutral-200 p-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{row.name}</span>
                    <Badge>{row.kind}</Badge>
                    <code className="font-mono text-xs text-neutral-500">
                      {row.secret_ref || "—"}
                    </code>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={testingId !== null}
                      onClick={() => void onTest(row)}
                    >
                      {testingId === row.id
                        ? t("projects.connections.testing")
                        : t("projects.connections.testBtn")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTestResult(null);
                        setDraft({
                          id: row.id,
                          name: row.name,
                          kind: row.kind,
                          configText: JSON.stringify(row.config, null, 2),
                          secretRef: row.secret_ref,
                        });
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      <span className="sr-only">
                        {t("projects.connections.editBtn")}
                      </span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void onDelete(row)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span className="sr-only">
                        {t("projects.connections.deleteBtn")}
                      </span>
                    </Button>
                  </div>
                </div>
                {testResult !== null && testResult.id === row.id && (
                  <div className="mt-2">
                    <Badge variant={testResult.result.ok ? "success" : "error"}>
                      {testResult.result.ok
                        ? t("projects.connections.testOk")
                        : t("projects.connections.testFailed")}
                    </Badge>
                    <pre
                      data-testid="connection-test-detail"
                      className="mt-1 overflow-x-auto rounded bg-neutral-50 p-2 font-mono text-xs"
                    >
                      {JSON.stringify(testResult.result.detail, null, 2)}
                    </pre>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {draft !== null && (
          <div className="mt-4 space-y-3 rounded border border-neutral-300 p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block text-xs">
                {t("projects.connections.nameLabel")}
                <Input
                  className="mt-1"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft({ ...draft, name: e.target.value })
                  }
                />
              </label>
              <Select
                label={t("projects.connections.kindLabel")}
                value={draft.kind}
                disabled={draft.id !== null}
                fullWidth
                onChange={(e) => {
                  const kind = e.target.value as ConnectionKind;
                  setDraft({
                    ...draft,
                    kind,
                    configText: CONFIG_TEMPLATES[kind],
                  });
                }}
                options={KINDS.map((k) => ({ value: k, label: k }))}
              />
            </div>
            <label className="block text-xs">
              {t("projects.connections.configLabel")}
              <textarea
                className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
                rows={7}
                value={draft.configText}
                onChange={(e) =>
                  setDraft({ ...draft, configText: e.target.value })
                }
              />
            </label>
            <label className="block text-xs">
              {t("projects.connections.secretRefLabel")}
              <Input
                className="mt-1 font-mono text-xs"
                value={draft.secretRef}
                placeholder={`vault:tenants/hp/connections/${
                  draft.name.trim() || "<name>"
                }`}
                onChange={(e) =>
                  setDraft({ ...draft, secretRef: e.target.value })
                }
              />
              <span className="mt-1 block text-xs text-neutral-500">
                {t("projects.connections.secretRefHint")}
              </span>
            </label>
            <p className="text-xs text-neutral-500">
              {t("projects.connections.noCredentialsNotice")}
            </p>
            <div className="flex gap-2">
              <Button size="sm" disabled={saving} onClick={() => void onSave()}>
                {t("projects.connections.saveBtn")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={saving}
                onClick={() => setDraft(null)}
              >
                {t("projects.connections.cancelBtn")}
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
